import React, { useEffect, useMemo, useState } from "react";
import { api, fmtUSD, fmtCompact, pdi, pdiTier } from "../../lib/api";
import { Funnel, Eye, MagnifyingGlass } from "@phosphor-icons/react";

const STAGE_META = {
  OBSERVE: { color: "sky",      desc: "Posted · response window open" },
  ORIENT:  { color: "amber",    desc: "Under evaluation · response closed" },
  DECIDE:  { color: "fuchsia",  desc: "Awarded · negotiating realized value" },
  ACT:     { color: "emerald",  desc: "Realized · savings booked" },
};
const COLOR_CLASS = {
  sky:     { bg: "bg-sky-500/10",     border: "border-sky-500/40",     fg: "text-sky-300",     bar: "bg-sky-500/70" },
  amber:   { bg: "bg-amber-500/10",   border: "border-amber-500/40",   fg: "text-amber-300",   bar: "bg-amber-500/70" },
  fuchsia: { bg: "bg-fuchsia-500/10", border: "border-fuchsia-500/40", fg: "text-fuchsia-300", bar: "bg-fuchsia-500/70" },
  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/40", fg: "text-emerald-300", bar: "bg-emerald-500/70" },
};
const TIER_CLASS = {
  GREEN: "tier-GREEN", YELLOW: "tier-YELLOW", ORANGE: "tier-ORANGE", RED: "tier-RED", BLACK: "tier-BLACK",
};

export default function OpportunitiesTab({ openProof, stageFocus }) {
  const [data, setData] = useState(null);
  const [stage, setStage] = useState("ALL");
  const [query, setQuery] = useState("");

  useEffect(() => { api.opportunitiesFunnel(200).then(setData).catch(() => setData({ funnel: [], opportunities: [], stages: [] })); }, []);
  useEffect(() => { if (stageFocus) setStage(stageFocus); }, [stageFocus]);

  const maxPipeline = useMemo(
    () => Math.max(1, ...((data?.funnel || []).map((f) => f.pipeline_value))),
    [data],
  );

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.opportunities.filter((o) => {
      if (stage === "PIPELINE") {
        if (!["OBSERVE", "ORIENT", "DECIDE"].includes(o.ooda_stage)) return false;
      } else if (stage !== "ALL" && o.ooda_stage !== stage) {
        return false;
      }
      if (query && !`${o.title} ${o.supplier || ""} ${o.agency} ${o.po_number || ""}`.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [data, stage, query]);

  if (!data) return <div className="h-[260px] skeleton rounded-sm" />;

  return (
    <div className="space-y-5" data-testid="tab-opportunities-content">
      {/* Funnel header */}
      <section className="bg-slate-900 border border-slate-800">
        <header className="px-5 py-3 border-b border-slate-800">
          <h2 className="font-display text-base font-semibold text-slate-50 flex items-center gap-2">
            <Funnel weight="duotone" className="text-emerald-400" size={16} />
            Opportunities Funnel · OODA Stages
          </h2>
          <p className="text-xs text-slate-500 mt-0.5 font-mono">
            Identified savings as they move through Observe → Orient → Decide → Act.
          </p>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-slate-800">
          {data.funnel.map((f) => {
            const meta = STAGE_META[f.stage] || {};
            const cls  = COLOR_CLASS[meta.color] || COLOR_CLASS.emerald;
            const widthPct = (f.pipeline_value / maxPipeline) * 100;
            return (
              <button
                key={f.stage}
                data-testid={`funnel-stage-${f.stage}`}
                onClick={() => setStage(f.stage)}
                className={`bg-slate-950 p-4 text-left flex flex-col gap-2 hover:bg-slate-900 transition-colors ${stage === f.stage ? "ring-1 ring-inset ring-emerald-500/40" : ""}`}
              >
                <div className="flex items-baseline justify-between">
                  <span className={`tracking-mono-label text-[10px] ${cls.fg}`}>{f.stage}</span>
                  <span className="font-mono text-[10px] text-slate-500">{f.count} ops</span>
                </div>
                <div className="font-mono text-xl text-slate-50">{fmtCompact(f.pipeline_value)}</div>
                <div className="font-mono text-[10px] text-slate-400">
                  gross {fmtCompact(f.gross_savings)} · decision-adj {fmtCompact(f.risk_adjusted_savings)}
                </div>
                <div className="text-[10px] text-slate-500 leading-snug">{meta.desc}</div>
                <div className="h-1.5 bg-slate-800 overflow-hidden rounded-sm mt-1">
                  <div className={`h-full ${cls.bar}`} style={{ width: `${Math.max(2, widthPct)}%` }} />
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Filter + search */}
      <section className="bg-slate-900 border border-slate-800">
        <header className="px-5 py-3 border-b border-slate-800 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-base font-semibold text-slate-50">Opportunity Ledger</h2>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">
              {filtered.length} of {data.opportunities.length} opportunities · filter by stage or supplier
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-slate-950 border border-slate-800 p-0.5 rounded-sm" data-testid="opp-stage-filter">
              {["ALL", "PIPELINE", ...data.stages].map((s) => (
                <button
                  key={s}
                  onClick={() => setStage(s)}
                  data-testid={`opp-filter-${s.toLowerCase()}`}
                  className={`px-2.5 py-1 text-[10px] tracking-mono-label rounded-sm transition-colors ${
                    stage === s ? "bg-emerald-500/20 text-emerald-300" : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 px-2 py-1 border border-slate-800 bg-slate-950 rounded-sm">
              <MagnifyingGlass size={12} className="text-slate-500" />
              <input
                data-testid="opp-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="search supplier · PO · agency"
                className="bg-transparent text-[11px] text-slate-100 placeholder:text-slate-600 outline-none w-44 font-mono"
              />
            </div>
          </div>
        </header>

        <div className="overflow-auto thin-scroll max-h-[560px]">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-950 z-10">
              <tr className="border-b border-slate-800 text-[10px] tracking-mono-label text-slate-500">
                <th className="px-4 py-2 text-left">Stage</th>
                <th className="px-4 py-2 text-left">SAM Notice</th>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-left">Agency</th>
                <th className="px-4 py-2 text-left">Supplier</th>
                <th className="px-4 py-2 text-right">Pipeline</th>
                <th className="px-4 py-2 text-right">Gross Δ</th>
                <th className="px-4 py-2 text-right">Decision-Adj Δ</th>
                <th className="px-4 py-2 text-center">PDI</th>
                <th className="px-4 py-2 text-center">Proof</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 100).map((o) => {
                const meta = STAGE_META[o.ooda_stage] || {};
                const cls  = COLOR_CLASS[meta.color] || COLOR_CLASS.emerald;
                const pipeline = o.estimated_value || o.baseline || 0;
                return (
                  <tr key={o.opportunity_id} data-testid={`opp-row-${o.opportunity_id}`} className="border-b border-slate-800 hover:bg-slate-800/50">
                    <td className="px-4 py-2"><span className={`px-1.5 py-0.5 rounded-sm text-[10px] tracking-mono-label font-mono ${cls.bg} ${cls.fg} border ${cls.border}`}>{o.ooda_stage}</span></td>
                    <td className="px-4 py-2 font-mono text-slate-400">{o.sam_notice_id}</td>
                    <td className="px-4 py-2 text-slate-200 max-w-[260px] truncate">{o.title}</td>
                    <td className="px-4 py-2 text-slate-400 max-w-[160px] truncate">{o.agency}</td>
                    <td className="px-4 py-2 text-slate-300 max-w-[180px] truncate">{o.supplier || <span className="text-slate-600">—</span>}</td>
                    <td className="px-4 py-2 font-mono text-right text-slate-300">{fmtCompact(pipeline)}</td>
                    <td className="px-4 py-2 font-mono text-right text-slate-400">{o.gross_savings ? fmtUSD(o.gross_savings) : <span className="text-slate-700">—</span>}</td>
                    <td className="px-4 py-2 font-mono text-right text-emerald-300">{o.risk_adjusted_savings ? fmtUSD(o.risk_adjusted_savings) : <span className="text-slate-700">—</span>}</td>
                    <td className="px-4 py-2 text-center">
                      {o.risk_score !== null ? (
                        <span
                          data-testid={`opp-pdi-${o.opportunity_id}`}
                          className={`inline-flex items-center justify-center w-12 h-9 rounded-md font-display text-base font-extrabold tabular-nums leading-none tier-${pdiTier(o.risk_score)}`}
                          title={`PDI ${pdi(o.risk_score)} · ${pdiTier(o.risk_score)} · Procurement Decision Intelligence`}
                        >
                          {pdi(o.risk_score)}
                        </span>
                      ) : <span className="text-slate-700">—</span>}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {o.po_id ? (
                        <button
                          data-testid={`opp-proof-${o.po_id}`}
                          onClick={() => openProof("SAVINGS", { ...o, po_id: o.po_id, po_number: o.po_number, delta: o.gross_savings, risk_adjusted_delta: o.risk_adjusted_savings })}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] tracking-mono-label text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 hover:border-emerald-500/60 rounded-sm transition-colors"
                        >
                          <Eye weight="bold" size={11} /> Proof
                        </button>
                      ) : <span className="text-slate-700 text-[10px]">no PO yet</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
